// The signed-in user's own view preferences, on the shared apiClient (base URL,
// auth, error semantics). Always the caller's OWN: the server takes the user from
// the token, so there is no user id to pass.
import { get, put } from './apiClient';

type Envelope<T> = { success: boolean; data: T };

/** A quotation grid column layout as the server stores it. */
export interface SavedQuotationGridLayout {
  /** Canonical column indices, in display order. */
  order: number[];
  /** Track widths ("130px"), in canonical order. */
  widths: string[];
  savedAt: string | null;
}

const QUOTATION_GRID_LAYOUT_PATH = '/users/preferences/quotation-grid-layout';

/**
 * The layout this user saved, or null when they never saved one. NOT checked
 * against the grid here — `gridLayout.usableLayout` knows the column count and
 * decides whether it still fits.
 */
export async function fetchSavedQuotationGridLayout(): Promise<SavedQuotationGridLayout | null> {
  const res = await get<Envelope<{ layout: SavedQuotationGridLayout | null }>>(QUOTATION_GRID_LAYOUT_PATH);
  return res?.data?.layout ?? null;
}

/**
 * Save this layout for this user, for every quotation. Throws the apiClient
 * error on refusal; `err.code === 'GRID_LAYOUT_INVALID'` when the server does
 * not accept it as a layout.
 */
export async function saveQuotationGridLayout(layout: {
  order: number[];
  widths: string[];
}): Promise<SavedQuotationGridLayout> {
  const res = await put<Envelope<{ layout: SavedQuotationGridLayout }>>(QUOTATION_GRID_LAYOUT_PATH, {
    order: layout.order,
    widths: layout.widths,
  });
  return res.data.layout;
}

/** D178: the tables whose column widths are saved, as the server's allow-list names them. */
export type ColumnWidthsTable = 'client-invoice-lines' | 'confirmed-order-lines';

const columnWidthsPath = (table: ColumnWidthsTable) => `/users/preferences/column-widths/${table}`;

/**
 * This user's saved widths for the table (`{ columnKey: px }`), or null when they never saved any.
 * Any other answer (apiClient hands back a proxy's HTML page as a string) throws: read as "never
 * saved", the next drag would overwrite their real widths (D178).
 */
export async function fetchSavedColumnWidths(table: ColumnWidthsTable): Promise<Record<string, number> | null> {
  const res = await get<Envelope<{ widths: Record<string, number> | null }>>(columnWidthsPath(table));
  const widths = res?.data?.widths;
  if (widths === null) return null;
  if (typeof widths !== 'object' || Array.isArray(widths)) throw new Error('Unreadable saved column widths');
  return widths;
}

/** Replace this user's saved widths for the table. Throws the apiClient error on refusal. */
export async function saveColumnWidths(table: ColumnWidthsTable, widths: Record<string, number>): Promise<void> {
  await put<Envelope<{ widths: Record<string, number> }>>(columnWidthsPath(table), { widths });
}
